import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import InvoiceDetails from 'components/modules/accounting/invoices/invoice-details';
import PayoutDetails from 'components/modules/accounting/payouts/payout-details';
import CargomaticModal from 'components/v2/modal';
import { getInvoicesForShipment, getPayoutsForShipment, } from 'utils/AccountingWebAPIUtils';
import CircleCheckIcon from 'components/v2/icons/CircleCheckIcon';
import Tooltip from 'components/v2/Tooltip';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { sluggify } from 'utils/localization/utils';
import DateField from './DateField';
import AdjustmentTitle from './AdjustmentTitle';
import formatMoney from 'utils/formatters/Money';
const RECEIVABLES = 'Receivables';
export default function PartnerCharges({ shipmentId, isIntermodalShipmentType, charges, type, timezone, total, }) {
    var _a;
    const { t } = useTranslation();
    const [lineItems, setLineItems] = useState([]);
    const [selectedLineItem, setSelectedLineItem] = useState();
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    function handleHideDetailsModal() {
        setShowDetailsModal(false);
    }
    async function getDocs() {
        try {
            if (type === RECEIVABLES) {
                const { entity: { invoices }, } = await getInvoicesForShipment(shipmentId);
                return invoices;
            }
            const { entity: { payouts }, } = await getPayoutsForShipment(shipmentId);
            return payouts;
        }
        catch (e) {
            console.log('error fetching payment documents', e);
        }
    }
    async function getInvoicesOrPayouts() {
        try {
            const payoutDocuments = await getDocs();
            let mappedLineItems;
            if (type === RECEIVABLES) {
                mappedLineItems = charges.map((lineItem) => {
                    const invoice = payoutDocuments.find((p) => p._id === lineItem.invoice_id);
                    if (!invoice) {
                        return Object.assign(Object.assign({}, lineItem), { hasInvoice: false, invoice: null });
                    }
                    const { _id, number, timestamp, status, email_sent } = invoice;
                    return Object.assign(Object.assign({}, lineItem), { hasInvoice: true, invoice: {
                            _id,
                            number,
                            timestamp,
                            status,
                            email_sent,
                        } });
                });
            }
            else {
                mappedLineItems = charges.map((lineItem) => {
                    const payout = payoutDocuments.find((p) => p._id === lineItem.payout_id);
                    if (!payout) {
                        return Object.assign(Object.assign({}, lineItem), { payout: null });
                    }
                    const { _id, number, timestamp, status, paid_date } = payout;
                    return Object.assign(Object.assign({}, lineItem), { payout: {
                            _id,
                            number,
                            timestamp,
                            status,
                            paid_date,
                        } });
                });
            }
            const sortedItems = mappedLineItems.sort((a, b) => a.payment_partner.name - b.payment_partner.name);
            setLineItems(sortedItems);
        }
        catch (e) {
            console.log('Error getting invoices', e);
        }
    }
    function handleShowDetails(_id) {
        return () => {
            setSelectedLineItem(_id);
            setShowDetailsModal(true);
        };
    }
    function renderLink(lineItem) {
        if (!lineItem.invoice && !lineItem.payout) {
            return null;
        }
        let _id;
        let number;
        if (type === RECEIVABLES) {
            _id = lineItem.invoice._id;
            number = lineItem.invoice.number;
        }
        else {
            _id = lineItem.payout._id;
            number = lineItem.payout.number;
        }
        return ((React.createElement("a", { "data-testid": "a-6bbf1a19", href: "#", onClick: handleShowDetails(_id) }, number)));
    }
    function getInvoiceOrPayoutDetails(selectedLineItem) {
        if (type === RECEIVABLES) {
            return React.createElement(InvoiceDetails, { invoiceId: selectedLineItem });
        }
        return React.createElement(PayoutDetails, { payoutId: selectedLineItem });
    }
    useEffect(() => {
        getInvoicesOrPayouts();
    }, [charges]);
    const paymentDocumentTitle = type === RECEIVABLES ? 'INVOICE' : 'PAYOUT';
    return ((React.createElement("div", { "data-testid": "div-38ebc91b" },
        React.createElement("div", { "data-testid": "div-cf801abd", className: "" },
            React.createElement("h3", { "data-testid": "h3-090dae7a", className: "my-0 mb-4 text-base" }, t(sluggify(`PARTNER_${type}`))),
            React.createElement("div", { "data-testid": "div-22f58f47", className: "table-wrapper overflow-x-auto" },
                React.createElement("table", { "data-testid": "table-4ede3a17", className: "default-table table-fixed" },
                    React.createElement("thead", { "data-testid": "thead-5c1145f5" },
                        React.createElement("tr", { "data-testid": "tr-9302fe83" },
                            React.createElement("th", { "data-testid": "th-4770a7b6", className: "w-48" }, t('CHARGE')),
                            React.createElement("th", { "data-testid": "th-6ab86513", className: "w-20" }, t('COST')),
                            React.createElement("th", { "data-testid": "th-f0bdc20d", className: "w-40" }, t('ENTERED_DATE')),
                            React.createElement("th", { "data-testid": "th-07cb31e9", className: "w-52" }, t(sluggify(paymentDocumentTitle), { count: 1 })),
                            React.createElement("th", { "data-testid": "th-12346e50", className: "w-40" }, t(sluggify(`${paymentDocumentTitle}_DATE`))),
                            React.createElement("th", { "data-testid": "th-a7ceec12", className: "w-5" }, t('SENT')),
                            React.createElement("th", { "data-testid": "th-63ece6e6", className: "w-52" }, titleize(t('CUSTOMER'))))),
                    React.createElement("tbody", { "data-testid": "tbody-feb3fe79" },
                        lineItems.map((lineItem, index) => {
                            var _a, _b, _c, _d, _e;
                            return (React.createElement("tr", { "data-testid": `tr-958c5cf7-${index}`, key: lineItem._id },
                                React.createElement("td", { "data-testid": `td-1a52d0e4-${index}`, className: "break-words" },
                                    React.createElement(AdjustmentTitle, { lineItem: lineItem, unapplied: isIntermodalShipmentType && !lineItem.equipment })),
                                React.createElement("td", { "data-testid": `td-c4453537-${index}` }, formatMoney(lineItem.value, lineItem === null || lineItem === void 0 ? void 0 : lineItem.currency)),
                                React.createElement("td", { "data-testid": `td-0356646f-${index}` },
                                    React.createElement(DateField, { date: lineItem.timestamp, timezone: timezone })),
                                React.createElement("td", { "data-testid": `td-7dc84d43-${index}` }, renderLink(lineItem)),
                                React.createElement("td", { "data-testid": `td-4cd1c35e-${index}` },
                                    React.createElement(DateField, { date: ((_a = lineItem.invoice) === null || _a === void 0 ? void 0 : _a.timestamp) ||
                                            ((_b = lineItem.payout) === null || _b === void 0 ? void 0 : _b.timestamp), timezone: timezone })),
                                React.createElement("td", { "data-testid": `td-055636a0-${index}` }, (((_c = lineItem.invoice) === null || _c === void 0 ? void 0 : _c.email_sent) ||
                                    ((_d = lineItem.payout) === null || _d === void 0 ? void 0 : _d.email_sent)) && (React.createElement(Tooltip, { dataTestId: `Tooltip-7c41d08b-${index}`, className: "w-5 h-5", body: () => (React.createElement(CircleCheckIcon, { className: "text-green-cm h-5 w-5" })) },
                                    React.createElement("div", { "data-testid": `div-0bf783f4-${index}` }, t('EMAIL_SENT'))))),
                                React.createElement("td", { "data-testid": `td-a15de3b1-${index}` },
                                    React.createElement("div", { "data-testid": `div-f60816c7-${index}`, className: "flex items-center space-x-2" }, (_e = lineItem.payment_partner) === null || _e === void 0 ? void 0 : _e.name))));
                        }),
                        React.createElement("tr", { "data-testid": "tr-5d7c7e49" },
                            React.createElement("td", { "data-testid": "td-c6433b3b" },
                                React.createElement("div", { "data-testid": "div-cf7d7ab5", className: "w-48" }, t('TOTAL'))),
                            React.createElement("td", { "data-testid": "td-203cf85a", className: "font-bold" }, formatMoney(total, (_a = lineItems[0]) === null || _a === void 0 ? void 0 : _a.currency)),
                            React.createElement("td", { "data-testid": "td-cf5c5688", colSpan: 5 }, "\u00A0")))))),
        showDetailsModal && (React.createElement(CargomaticModal, { dataTestId: "CargomaticModal-a8da7b03", title: "", show: showDetailsModal, footer: null, body: getInvoiceOrPayoutDetails(selectedLineItem), onHide: handleHideDetailsModal, dialogClassName: "modal-override", location: "top" })))));
}
PartnerCharges.propTypes = {
    shipmentId: PropTypes.string.isRequired,
    timezone: PropTypes.string,
    isIntermodalShipmentType: PropTypes.bool,
};
PartnerCharges.defaultProps = {
    timezone: 'America/Los_Angeles',
};
